import { Injectable } from '@angular/core';
import { SentryService } from './sentry.service';

@Injectable({
  providedIn: 'root'
})
export class PerformanceMonitorService {

  private performanceMetrics: { [key: string]: number } = {};

  constructor(private sentry: SentryService) {}

  /**
   * Monitor Sentry's performance impact
   */
  measureSentryImpact(operationName: string, operation: () => Promise<any> | any): Promise<any> {
    const startTime = performance.now();
    const startMemory = (performance as any).memory?.usedJSHeapSize || 0;

    try {
      const result = operation();

      if (result instanceof Promise) {
        return result.finally(() => {
          this.recordMetrics(operationName, startTime, startMemory);
        });
      } else {
        this.recordMetrics(operationName, startTime, startMemory);
        return Promise.resolve(result);
      }
    } catch (error) {
      this.recordMetrics(operationName, startTime, startMemory);
      throw error;
    }
  }

  private recordMetrics(operationName: string, startTime: number, startMemory: number): void {
    const endTime = performance.now();
    const endMemory = (performance as any).memory?.usedJSHeapSize || 0;

    const metrics = {
      duration: endTime - startTime,
      memoryDelta: endMemory - startMemory,
      timestamp: Date.now()
    };

    this.performanceMetrics[operationName] = metrics.duration;

    // Log performance impact if significant
    if (metrics.duration > 100) { // More than 100ms
      console.warn(`[Performance] ${operationName} took ${metrics.duration.toFixed(2)}ms`);

      // Only report to Sentry if impact is severe (>500ms)
      if (metrics.duration > 500) {
        this.sentry.captureMessage(
          `Performance issue: ${operationName} took ${metrics.duration.toFixed(2)}ms`,
          'warning'
        );
      }
    }

    if (metrics.memoryDelta > 1024 * 1024) { // More than 1MB
      console.warn(`[Memory] ${operationName} used ${(metrics.memoryDelta / 1024 / 1024).toFixed(2)}MB`);
    }
  }

  /**
   * Get performance statistics
   */
  getPerformanceStats(): { [key: string]: number } {
    return { ...this.performanceMetrics };
  }

  /**
   * Monitor page load performance
   */
  monitorPageLoad(): void {
    if (typeof window !== 'undefined' && 'performance' in window) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
          if (navigation) {
            const loadTime = navigation.loadEventEnd - navigation.fetchStart;
            const domReady = navigation.domContentLoadedEventEnd - navigation.fetchStart;

            console.log(`[Performance] Page load: ${loadTime.toFixed(2)}ms, DOM ready: ${domReady.toFixed(2)}ms`);

            // Report slow page loads
            if (loadTime > 3000) { // More than 3 seconds
              this.sentry.addBreadcrumb({
                message: `Slow page load: ${loadTime.toFixed(2)}ms`,
                category: 'performance',
                level: 'warning'
              });
            }
          }
        }, 0);
      });
    }
  }

  /**
   * Monitor frame rate drops
   */
  monitorFrameRate(): void {
    if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
      let lastTime = performance.now();
      let frameCount = 0;
      let fpsArray: number[] = [];

      const measureFPS = () => {
        const currentTime = performance.now();
        const delta = currentTime - lastTime;
        frameCount++;

        if (delta >= 1000) { // Every second
          const fps = Math.round((frameCount * 1000) / delta);
          fpsArray.push(fps);

          if (fpsArray.length > 60) fpsArray.shift(); // Keep last 60 seconds

          const avgFPS = fpsArray.reduce((a, b) => a + b, 0) / fpsArray.length;

          // Report low FPS
          if (avgFPS < 30) {
            console.warn(`[Performance] Low FPS detected: ${avgFPS.toFixed(1)}`);
            this.sentry.addBreadcrumb({
              message: `Low FPS: ${avgFPS.toFixed(1)}`,
              category: 'performance',
              level: 'warning'
            });
          }

          frameCount = 0;
          lastTime = currentTime;
        }

        requestAnimationFrame(measureFPS);
      };

      requestAnimationFrame(measureFPS);
    }
  }
}